import type { RouteLocationNormalized, RouteRecordNormalized } from 'vue-router'
import type { App, Component } from 'vue'
import { intersectionWith, isEqual, mergeWith, toNumber, unionWith } from 'lodash-es'
import { unref } from 'vue'
import { isArray, isObject } from '@/utils/is'

export function noop() { }

/**
 * @description:  Set ui mount node
 */
export function getPopupContainer(node?: HTMLElement): HTMLElement {
  return (node?.parentNode as HTMLElement) ?? document.body
}

/**
 * Add the object as a parameter to the URL
 * @param baseUrl url
 * @param obj
 * @returns {string}
 * eg:
 *  let obj = {a: '3', b: '4'}
 *  setObjToUrlParams('www.baidu.com', obj)
 *  ==>www.baidu.com?a=3&b=4
 */
export function setObjToUrlParams(baseUrl: string, obj: any): string {
  let parameters = ''
  for (const key in obj)
    parameters += `${key}=${encodeURIComponent(obj[key])}&`

  parameters = parameters.replace(/&$/, '')
  return /\?$/.test(baseUrl) ? baseUrl + parameters : baseUrl.replace(/\/?$/, '?') + parameters
}

/**
 * 分转元
 * @param num
 */
export function convertToInteger(num: any): any {
  if (num === 0)
    return 0.00
  if (num === 'undefined' || num === 'null')
    return ''
  const str = `${num / 100}`
  const intSum = str
    .substring(0, str.indexOf('.'))
    .replace(/\B(?=(?:\d{3})+$)/g, ',') // 取到整数部分
  const dot = str.substring(str.length, str.indexOf('.')) // 取到小数部分搜索
  const ret = intSum + dot
  const cleaned = ret.replace(/[^\d.-]/g, '')
  // 将清理后的字符串转换为数字
  const number = Number.parseFloat(cleaned)
  // return number;
  return number
  // if (typeof num === 'undefined')
  //   return 0
  // const parsedNumber = typeof num === 'string' ? Number.parseFloat(num) : num
  // TODO 元转分后还有小数则四舍五入
  // return Math.round(parsedNumber) * 100
}

/**
 * 元转分
 * @param num
 */
export function integerToConvert(num: number | string | undefined): number {
  if (typeof num === 'undefined')
    return 0
  const parsedNumber = typeof num === 'string' ? Number.parseFloat(num) : num
  // TODO 元转分后还有小数则四舍五入
  return Math.round(parsedNumber * 100)
}

/**
 * Recursively merge two objects.
 * 递归合并两个对象。
 *
 * @param source The source object to merge from. 要合并的源对象。
 * @param target The target object to merge into. 目标对象，合并后结果存放于此。
 * @param mergeArrays How to merge arrays. Default is "replace".
 *        如何合并数组。默认为replace。
 *        - "union": Union the arrays. 对数组执行并集操作。
 *        - "intersection": Intersect the arrays. 对数组执行交集操作。
 *        - "concat": Concatenate the arrays. 连接数组。
 *        - "replace": Replace the source array with the target array. 用目标数组替换源数组。
 * @returns The merged object. 合并后的对象。
 */
export function deepMerge<T extends object | null | undefined, U extends object | null | undefined>(
  source: T,
  target: U,
  mergeArrays: 'union' | 'intersection' | 'concat' | 'replace' = 'replace',
): T & U {
  if (!target)
    return source as T & U

  if (!source)
    return target as T & U

  return mergeWith({}, source, target, (sourceValue, targetValue) => {
    if (isArray(targetValue) && isArray(sourceValue)) {
      switch (mergeArrays) {
        case 'union':
          return unionWith(sourceValue, targetValue, isEqual)
        case 'intersection':
          return intersectionWith(sourceValue, targetValue, isEqual)
        case 'concat':
          return sourceValue.concat(targetValue)
        case 'replace':
          return targetValue
        default:
          throw new Error(`Unknown merge array strategy: ${mergeArrays as string}`)
      }
    }
    if (isObject(targetValue) && isObject(sourceValue))
      return deepMerge(sourceValue, targetValue, mergeArrays)

    return undefined
  })
}

export function openWindow(
  url: string,
  opt?: { target?: TargetContext | string, noopener?: boolean, noreferrer?: boolean },
) {
  const { target = '__blank', noopener = true, noreferrer = true } = opt || {}
  const feature: string[] = []

  noopener && feature.push('noopener=yes')
  noreferrer && feature.push('noreferrer=yes')

  window.open(url, target, feature.join(','))
}

// dynamic use hook props
export function getDynamicProps<T extends Record<string, unknown>, U>(props: T): Partial<U> {
  const ret: Recordable = {}

  Object.keys(props).map((key) => {
    return ret[key] = unref((props as Recordable)[key])
  })

  return ret as Partial<U>
}

export function getRawRoute(route: RouteLocationNormalized): RouteLocationNormalized {
  if (!route)
    return route
  const { matched, ...opt } = route
  return {
    ...opt,
    matched: (matched
      ? matched.map(item => ({
        meta: item.meta,
        name: item.name,
        path: item.path,
      }))
      : undefined) as RouteRecordNormalized[],
  }
}

// https://github.com/vant-ui/vant/issues/8302
interface EventShim {
  new(...args: any[]): {
    $props: {
      onClick?: (...args: any[]) => void
    }
  }
}

export type WithInstall<T> = T & {
  install(app: App): void
} & EventShim

export type CustomComponent = Component & { displayName?: string }

export function withInstall<T extends CustomComponent>(component: T, alias?: string) {
  (component as Record<string, unknown>).install = (app: App) => {
    const compName = component.name || component.displayName
    if (!compName)
      return
    app.component(compName, component)
    if (alias)
      app.config.globalProperties[alias] = component
  }
  return component as WithInstall<T>
}

/**
 * 简单实现防抖方法
 *
 * 防抖(debounce)函数在第一次触发给定的函数时，不立即执行函数，而是给出一个期限值(delay)，比如100ms。
 * 如果100ms内再次执行函数，就重新开始计时，直到计时结束后再真正执行函数。
 * 这样做的好处是如果短时间内大量触发同一事件，只会执行一次函数。
 *
 * @param fn 要防抖的函数
 * @param delay 防抖的毫秒数
 * @returns {Function} simpleDebounce
 */
export function simpleDebounce(fn, delay = 100) {
  let timer: any | null = null
  return () => {
    // eslint-disable-next-line prefer-rest-params
    const args = arguments
    if (timer)
      clearTimeout(timer)

    timer = setTimeout(() => {
      // eslint-disable-next-line ts/no-invalid-this
      fn.apply(this, args)
    }, delay)
  }
}

/**
 * 将一个整数转换为分数保留两位小数
 * @param num
 */
export function formatToFraction(num: number | string | undefined): number {
  if (typeof num === 'undefined')
    return 0
  const parsedNumber = typeof num === 'string' ? Number.parseFloat(num) : num
  return Number.parseFloat((parsedNumber / 100).toFixed(2))
}

/**
 * 将一个数转换为 1.00 这样
 * 数据呈现的时候使用
 *
 * @param num 整数
 */
export function floatToFixed2(num: number | string | undefined): string {
  let str = '0.00'
  if (typeof num === 'undefined')
    return str

  const f = formatToFraction(num)
  const decimalPart = f.toString().split('.')[1]
  const len = decimalPart ? decimalPart.length : 0
  switch (len) {
    case 0:
      str = `${f.toString()}.00`
      break
    case 1:
      str = `${f.toString()}0`
      break
  }
  return str
}
/**
 * 将一个金额转换为 1.00 这样
 * 数据呈现的时候使用
 *
 * @param amount 整数
 */

export function formatAmount(amount) {
  if (Number.isNaN(amount))
    return '￥0.00' // 如果金额不是数字或无法转换为数字，则返回0.00
  // 判断是否为整数
  if (Number.isInteger(amount)) {
    return `￥${Number(amount).toFixed(2)}` // 如果是整数，直接转换为字符串
  }
  else {
    // 如果不是整数，保留两位小数并转换为字符串
    return `￥${Number(amount).toFixed(2)}`
  }

  // return `￥${Number(amount).toFixed(2)}` // 使用toFixed方法保留两位小数
};

export function formatAmountCopy(amount) {
  if (Number.isNaN(amount))
    return '￥0.00' // 如果金额不是数字或无法转换为数字，则返回0.00
  // 判断是否为整数
  if (Number.isInteger(amount)) {
    return `￥${Number(amount).toString()}.00` // 如果是整数，直接转换为字符串 并且附带.00
  }
  else {
    // 如果不是整数，保留两位小数并转换为字符串
    return `￥${Number(amount).toFixed(2)}`
  }

  // return `￥${Number(amount).toFixed(2)}` // 使用toFixed方法保留两位小数
};

export function require(imgPath) {
  try {
    const handlePath = imgPath.replace('@', '..')
    // https://vitejs.cn/guide/assets.html#the-public-directory
    return new URL(handlePath, import.meta.url).href
  }
  catch (error) {
    console.warn(error)
  }
}

/**
 * 获取链接的参数值（值类型）
 * @param key 参数键名
 * @param urlStr 链接地址，默认为当前浏览器的地址
 */
export function getUrlNumberValue(key: string, urlStr: string = location.href): number {
  return toNumber(getUrlValue(key, urlStr))
}

/**
 * 获取链接的参数值
 * @param key 参数键名
 * @param urlStr 链接地址，默认为当前浏览器的地址
 */
export function getUrlValue(key: string, urlStr: string = location.href): string {
  if (!urlStr || !key)
    return ''
  const url = new URL(decodeURIComponent(urlStr))
  return url.searchParams.get(key) ?? ''
}

/**
 * @description 格式化时间
 * @param {string | number} dateTime 需要格式化的时间戳
 * @param {string} fmt 格式化规则 yyyy:mm:dd|yyyy:mm|yyyy年mm月dd日|yyyy年mm月dd日 hh时MM分等,可自定义组合 默认yyyy-mm-dd
 * @returns {string} 返回格式化后的字符串
 */
export function timeFormat(dateTime, formatStr = 'yyyy-mm-dd') {
  let date
  // 若传入时间为假值，则取当前时间
  if (!dateTime) {
    date = new Date()
  }
  // 若为unix秒时间戳，则转为毫秒时间戳（逻辑有点奇怪，但不敢改，以保证历史兼容）
  else if (/^\d{10}$/.test(dateTime?.toString().trim())) {
    date = new Date(dateTime * 1000)
  }
  // 若用户传入字符串格式时间戳，new Date无法解析，需做兼容
  else if (typeof dateTime === 'string' && /^\d+$/.test(dateTime?.trim())) {
    date = new Date(Number(dateTime))
  }
  // 其他都认为符合 RFC 2822 规范
  else {
    // 处理平台性差异，在Safari/Webkit中，new Date仅支持/作为分割符的字符串时间
    date = new Date(typeof dateTime === 'string' ? dateTime?.replace(/-/g, '/') : dateTime)
  }

  const timeSource = {
    y: date.getFullYear().toString(), // 年
    m: (date.getMonth() + 1).toString().padStart(2, '0'), // 月
    d: date.getDate().toString().padStart(2, '0'), // 日
    h: date.getHours().toString().padStart(2, '0'), // 时
    M: date.getMinutes().toString().padStart(2, '0'), // 分
    s: date.getSeconds().toString().padStart(2, '0'), // 秒
    // 有其他格式化字符需求可以继续添加，必须转化成字符串
  }

  for (const key in timeSource) {
    const [ret] = new RegExp(`${key}+`).exec(formatStr) || []
    if (ret) {
      // 年可能只需展示两位
      const beginIndex = key === 'y' && ret.length === 2 ? 2 : 0
      formatStr = formatStr.replace(ret, timeSource[key].slice(beginIndex))
    }
  }

  return formatStr
}
